import { areUnitsIncompatible, parseLeadingQuantityPrefix } from './catalog-position-match';

/**
 * Softecs Großhandelskatalog (DATANORM-Import) liefert `price` als Preis PRO N
 * EINHEITEN, wobei N als führende Zahl im Feld `price_unit` (Preiseinheit)
 * steht: "ST" (N=1), "100 ST", "100 M", "1000 ST" — verifiziert gegen
 * Fortuna_Softec/lib/services/datanorm-parser.ts::formatDelimitedPriceUnit,
 * die genau dieses `"${10 ** exponent} ${quantityUnit}"`-Format erzeugt.
 *
 * ~18% der echten Katalogzeilen haben N>1 (v.a. Meterware/Kleinteile). Ohne
 * Division durch N würde eine Übernahme in eine Kalkulationszeile den EK um
 * den Faktor N zu hoch ansetzen — dieselbe Fehlerklasse wie der historische
 * EP-Bug (522 € ÷ 10 = 52,20 statt 522), hier nur mit umgekehrtem Vorzeichen
 * des Fehlers (zu hoch statt zu niedrig, wenn man N ignoriert).
 *
 * Einheiten-Kompatibilität: der Text NACH der Zahl in `price_unit` (z.B. "M"
 * bei "100 M") wird gegen `quantityUnit` verglichen — mit derselben
 * Einheiten-Klassen-Tabelle wie shared/catalog-position-match.ts
 * (Länge/Fläche/Volumen/Gewicht/Stück/Gebinde), damit es nur EINE Quelle für
 * "was ist eine kompatible Einheit" gibt. "unbekannt ≠ Veto" gilt auch hier:
 * fehlt eine Seite, wird NICHT gewarnt — nur ein weiches Flag, nie ein
 * Hard-Fail (Bestandsdaten/Zahlen werden nie leichtfertig verworfen).
 */

export interface EffectiveUnitPriceInput {
  /** Katalogpreis für N Einheiten (Softecs netPrice/listPrice). Robust gegen
   *  Zahl ODER String mit Komma-Dezimaltrennzeichen. */
  price: number | string | null | undefined;
  /** Softecs `price_unit`, z.B. "100 M", "1000 ST", "ST", "" oder null. */
  priceUnit?: string | null;
  /** Softecs `quantity_unit` (im Suchergebnis als `unit` gemappt). */
  quantityUnit?: string | null;
}

export interface EffectiveUnitPriceResult {
  /**
   * price / N — der Preis für EINE Einheit. Nur auf 6 Nachkommastellen
   * gerundet, um Fließkomma-Rauschen zu entfernen (KEINE Geld-Rundung; die
   * passiert erst downstream über shared/kalkulation.ts::roundMoney, wenn der
   * Wert tatsächlich in eine Kalkulationszeile fließt).
   * null, wenn `price` fehlt/ungültig war.
   */
  value: number | null;
  /** Der aus `priceUnit` geparste Divisor N (Default 1, nie 0). */
  n: number;
  /** Der Einheiten-Textteil von `priceUnit` NACH der führenden Zahl (z.B. "M"
   *  bei "100 M"), oder null wenn kein Text vorhanden war. */
  unitLabel: string | null;
  /**
   * true, wenn `unitLabel` und `quantityUnit` beide einer bekannten, aber
   * UNTERSCHIEDLICHEN Einheiten-Klasse angehören (z.B. "ST" vs. "m") —
   * Datenqualitäts-Warnung für die UI, kein Hard-Fail.
   */
  unitsIncompatible: boolean;
}

function parsePriceNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * P1-1 (Fund): delegiert an catalog-position-match.ts::parseLeadingQuantityPrefix — DORT
 * jetzt die einzige Quelle für diese Parse-Logik (auch normalizeUnitClass strippt darüber
 * dieselbe führende Mengensyntax, z.B. wenn ein Katalog-Kandidat seine `quantityUnit` als
 * unverarbeiteten Preiseinheit-String wie "100 ST" trägt). Zwei separat gepflegte
 * Regex-Kopien hätten sonst auseinanderdriften können.
 */
function parsePriceUnit(priceUnit: string | null | undefined): { n: number; unitLabel: string | null } {
  const trimmed = (priceUnit ?? '').trim();
  if (trimmed === '') return { n: 1, unitLabel: null };
  const { n, rest } = parseLeadingQuantityPrefix(trimmed);
  return { n, unitLabel: rest };
}

/**
 * Rechnet Softecs "Preis pro N Einheiten" in einen effektiven Preis PRO EINER
 * Einheit um. Reine Funktion, kein DB-/Store-Zugriff.
 */
export function effectiveUnitPrice(input: EffectiveUnitPriceInput): EffectiveUnitPriceResult {
  const { n, unitLabel } = parsePriceUnit(input.priceUnit);
  const price = parsePriceNumber(input.price);
  const value = price === null ? null : Math.round((price / n) * 1e6) / 1e6;
  const unitsIncompatible = areUnitsIncompatible(unitLabel, input.quantityUnit);

  return { value, n, unitLabel, unitsIncompatible };
}

/**
 * P2-5 (Fix, Codex-Review gpt-5.6): Anzeige-Einheit für den NACH-DIVISOR-Wert
 * (effectiveUnitPrice(...).value). Bei Fixed-Width-DATANORM-Importen kann `quantityUnit`
 * (der naheliegende Anzeige-Fallback) selbst den ROHEN Preiseinheit-Text tragen (z.B. "100 ST"
 * statt "ST", ein Datenqualitätsartefakt der Fixed-Width-Zeilenaufteilung) — `eff.value` ist
 * bereits durch den in `priceUnit` geparsten Divisor N geteilt, ein damit gerendertes "100 ST"
 * würde also einen bereits durch 100 geteilten Preis erneut als "je 100 ST" ausgeben (falscher
 * Anwendungswert). `unitLabel` (aus `priceUnit` geparst, IMMER schon vom Divisor-Präfix bereinigt)
 * ist deshalb die verlässlichere Quelle und hat Vorrang — der `fallback` (z.B. `quantityUnit`,
 * `position.einheit`) greift nur, wenn `priceUnit` gar keinen Text-Teil hatte (unitLabel null).
 */
export function nachDivisorEinheitLabel(
  eff: Pick<EffectiveUnitPriceResult, 'unitLabel'>,
  fallback: string | null | undefined,
): string {
  return eff.unitLabel || fallback || 'Einheit';
}
